import { FeatureFlag, FeatureFlagId, FeatureFlagConfig } from './types';

const STORAGE_KEY = 'dadan_feature_flags';
const ENV_PREFIX = 'REACT_APP_FEATURE_';

export class FeatureFlagService {
  private flags: Map<string, FeatureFlag> = new Map();
  private userId?: string;

  constructor(userId?: string) {
    this.userId = userId;
    this.loadFlags();
  }

  private loadFlags(): void {
    const defaultFlags: FeatureFlag[] = [
      {
        id: 'indexeddb-v5',
        enabled: this.getEnvFlag('INDEXEDDB_V5', true),
        rolloutPercentage: 100,
      },
      {
        id: 'worker-v2',
        enabled: this.getEnvFlag('WORKER_V2', true),
        rolloutPercentage: 100,
      },
      {
        id: 'background-sync',
        enabled: this.getEnvFlag('BACKGROUND_SYNC', true),
        rolloutPercentage: 100,
      },
      {
        id: 'templates-ui',
        enabled: this.getEnvFlag('TEMPLATES_UI', true),
        rolloutPercentage: 100,
      },
      {
        id: 'telemetry',
        enabled: this.getEnvFlag('TELEMETRY', true),
        rolloutPercentage: 100,
      },
      {
        id: 'parallel-upload',
        enabled: this.getEnvFlag('PARALLEL_UPLOAD', true),
        rolloutPercentage: 100,
      },
      {
        id: 'upload-compression',
        enabled: this.getEnvFlag('UPLOAD_COMPRESSION', true),
        rolloutPercentage: 100,
      },
      {
        id: 'scene-timeline',
        enabled: this.getEnvFlag('SCENE_TIMELINE', true),
        rolloutPercentage: 100,
      },
      {
        id: 'ai-script-generation',
        enabled: this.getEnvFlag('AI_SCRIPT_GENERATION', true),
        rolloutPercentage: 100,
      },
      {
        id: 'platform-export',
        enabled: this.getEnvFlag('PLATFORM_EXPORT', true),
        rolloutPercentage: 100,
      },
      {
        id: 'custom-templates',
        enabled: this.getEnvFlag('CUSTOM_TEMPLATES', true),
        rolloutPercentage: 100,
      },
      {
        id: 'ai-delivery-coaching',
        enabled: this.getEnvFlag('AI_DELIVERY_COACHING', false),
        rolloutPercentage: 0,
      },
      // Phase 1: Critical Business Features (ENABLED 100%)
      {
        id: 'user-permissions-system',
        enabled: this.getEnvFlag('USER_PERMISSIONS_SYSTEM', true),
        rolloutPercentage: 100,
      },
      {
        id: 'recording-duration-limits',
        enabled: this.getEnvFlag('RECORDING_DURATION_LIMITS', true),
        rolloutPercentage: 100,
      },
      {
        id: 'resolution-limits',
        enabled: this.getEnvFlag('RESOLUTION_LIMITS', true),
        rolloutPercentage: 100,
      },
      {
        id: 'device-quality-tiers',
        enabled: this.getEnvFlag('DEVICE_QUALITY_TIERS', true),
        rolloutPercentage: 100,
      },
      // Phase 2: Audio & Recording Quality (ENABLED 100%)
      {
        id: 'rnnoise-noise-suppression',
        enabled: this.getEnvFlag('RNNOISE_NOISE_SUPPRESSION', true),
        rolloutPercentage: 100,
      },
      {
        id: 'dual-audio-processor',
        enabled: this.getEnvFlag('DUAL_AUDIO_PROCESSOR', true),
        rolloutPercentage: 100,
      },
      {
        id: 'chunk-accumulation',
        enabled: this.getEnvFlag('CHUNK_ACCUMULATION', true),
        rolloutPercentage: 100,
      },
      {
        id: 'idb-writer-worker',
        enabled: this.getEnvFlag('IDB_WRITER_WORKER', true),
        rolloutPercentage: 100,
      },
      // Phase 3: UX & Browser Compatibility (ENABLED 100%)
      {
        id: 'browser-detection-overlay',
        enabled: this.getEnvFlag('BROWSER_DETECTION_OVERLAY', true),
        rolloutPercentage: 100,
      },
      {
        id: 'device-capability-hints',
        enabled: this.getEnvFlag('DEVICE_CAPABILITY_HINTS', true),
        rolloutPercentage: 100,
      },
      {
        id: 'upgrade-prompts',
        enabled: this.getEnvFlag('UPGRADE_PROMPTS', true),
        rolloutPercentage: 100,
      },
    ];

    const storedFlags = this.loadFromLocalStorage();
    const envFlags = new Map(defaultFlags.map(f => [f.id, f]));

    storedFlags.forEach(flag => {
      envFlags.set(flag.id, { ...envFlags.get(flag.id), ...flag });
    });

    this.flags = envFlags;
  }

  private getEnvFlag(name: string, defaultValue: boolean): boolean {
    const envValue = process.env[`${ENV_PREFIX}${name}`];
    if (envValue === undefined) return defaultValue;
    return envValue === 'true' || envValue === '1';
  }

  private loadFromLocalStorage(): FeatureFlag[] {
    try {
      // Check if localStorage is available (not available in Web Workers)
      if (typeof localStorage === 'undefined') {
        return [];
      }
      
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return [];
      const config: FeatureFlagConfig = JSON.parse(stored);
      return Object.values(config.flags);
    } catch (error) {
      console.warn('Failed to load feature flags from localStorage:', error);
      return [];
    }
  }

  private saveToLocalStorage(): void {
    try {
      // Check if localStorage is available (not available in Web Workers)
      if (typeof localStorage === 'undefined') {
        return;
      }
      
      const config: FeatureFlagConfig = {
        flags: Object.fromEntries(this.flags.entries()),
        userId: this.userId,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    } catch (error) {
      console.error('Failed to save feature flags to localStorage:', error);
    }
  }

  isEnabled(flagId: FeatureFlagId): boolean {
    const flag = this.flags.get(flagId);
    if (!flag) return false;

    if (flag.overrides && this.userId && this.userId in flag.overrides) {
      return flag.overrides[this.userId];
    }

    if (flag.rolloutPercentage > 0 && flag.rolloutPercentage < 100) {
      return this.isInRollout(flagId, flag.rolloutPercentage);
    }

    return flag.enabled;
  }

  private isInRollout(flagId: string, percentage: number): boolean {
    if (!this.userId) return false;
    
    const hash = this.hashString(`${flagId}:${this.userId}`);
    const bucket = hash % 100;
    return bucket < percentage;
  }

  private hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash);
  }

  getFlag(flagId: FeatureFlagId): FeatureFlag | null {
    return this.flags.get(flagId) || null;
  }

  getAllFlags(): FeatureFlag[] {
    return Array.from(this.flags.values());
  }

  setFlag(flagId: FeatureFlagId, enabled: boolean): void {
    const flag = this.flags.get(flagId);
    if (flag) {
      flag.enabled = enabled;
      this.flags.set(flagId, flag);
      this.saveToLocalStorage();
    }
  }

  setRolloutPercentage(flagId: FeatureFlagId, percentage: number): void {
    const flag = this.flags.get(flagId);
    if (flag) {
      flag.rolloutPercentage = Math.max(0, Math.min(100, percentage));
      this.flags.set(flagId, flag);
      this.saveToLocalStorage();
    }
  }

  setUserOverride(flagId: FeatureFlagId, userId: string, enabled: boolean): void {
    const flag = this.flags.get(flagId);
    if (flag) {
      if (!flag.overrides) flag.overrides = {};
      flag.overrides[userId] = enabled;
      this.flags.set(flagId, flag);
      this.saveToLocalStorage();
    }
  }

  reload(): void {
    this.loadFlags();
  }
}
